import * as React from "react";
import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { LoadingState } from "../../components/patterns/states";
import type { Attachment } from "../../backend/api/chat";
import { attachmentThumbnailUrl, attachmentUrl } from "../../backend/api/chat";
import { TOUCH_CLOSE } from "./dialogClose";
import { useFocusReturn } from "./useFocusReturn";

export type MediaLightboxProps = {
  attachment: Attachment | null;
  onClose: () => void;
};

type LoadStatus = "checking" | "ready" | "expired" | "failed";

/**
 * Full-size view of one shared attachment.
 *
 * The source is checked with a HEAD request before it is handed to an
 * `<img>`/`<video>` tag, because those tags cannot tell a caller whether a
 * failed load was a plain error or an attachment the retention policy has
 * expired (410) - and the two need different copy.
 */
export function MediaLightbox({ attachment, onClose }: MediaLightboxProps) {
  const focusReturn = useFocusReturn(attachment !== null);
  const [status, setStatus] = useState<LoadStatus>("checking");
  const name = attachment?.original_name || "Untitled file";

  useEffect(() => {
    if (!attachment) return;
    setStatus("checking");
    let cancelled = false;

    fetch(attachmentUrl(attachment.id), { method: "HEAD", credentials: "include" })
      .then((response) => {
        if (cancelled) return;
        if (response.status === 410) {
          setStatus("expired");
        } else if (!response.ok) {
          setStatus("failed");
        } else {
          setStatus("ready");
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });

    return () => {
      cancelled = true;
    };
  }, [attachment]);

  return (
    <Dialog
      open={attachment !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="max-w-4xl grid-cols-[minmax(0,1fr)]"
          closeClassName={TOUCH_CLOSE}
        {...focusReturn}
      >
        {attachment && (
          <>
            <DialogHeader>
              <DialogTitle className="truncate pr-8">{name}</DialogTitle>
              <DialogDescription className="sr-only">
                Preview of {name}
              </DialogDescription>
            </DialogHeader>

            <div className="flex min-h-[40vh] items-center justify-center">
              {status === "checking" ? (
                <LoadingState label="Loading media" />
              ) : status === "expired" ? (
                <p className="max-w-sm text-center text-sm text-muted-foreground">
                  This file is no longer available. Shared media expires after
                  a while and cannot be recovered.
                </p>
              ) : status === "failed" ? (
                <p className="max-w-sm text-center text-sm text-muted-foreground">
                  This file could not be loaded. Try again in a moment.
                </p>
              ) : attachment.kind === "video" ? (
                <video
                  controls
                  poster={attachmentThumbnailUrl(attachment.id)}
                  src={attachmentUrl(attachment.id)}
                  // Development serves the API from another origin, and media
                  // elements send no cookies cross-origin unless asked.
                  crossOrigin="use-credentials"
                  onError={() => setStatus("failed")}
                  className="max-h-[70vh] w-full rounded-md"
                >
                  <track kind="captions" />
                </video>
              ) : (
                <img
                  src={attachmentUrl(attachment.id)}
                  alt={name}
                  crossOrigin="use-credentials"
                  onError={() => setStatus("failed")}
                  className="max-h-[70vh] w-full rounded-md object-contain"
                />
              )}
            </div>

            <div className="flex justify-end">
              <Button
                asChild
                variant="outline"
                className="min-h-touch md:min-h-0"
              >
                <a href={attachmentUrl(attachment.id)} download={name}>
                  <Download className="h-4 w-4" aria-hidden="true" />
                  Download
                </a>
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
